import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

/**
 * Lands after the admin clicks the email link for "Confirm password change".
 * Hits POST /api/auth/confirm-password-change?token=… and renders a clear
 * success / expired / invalid state with a link back to the login screen.
 *
 * Public page — does NOT require an authenticated session.
 */
export default function ConfirmPasswordChange() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | expired | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("error");
      setMessage("This confirmation link is missing its token.");
      return;
    }
    api
      .post("/auth/confirm-password-change", { token })
      .then(({ data }) => {
        if (cancelled) return;
        setState("success");
        setEmail(data?.email || "");
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 410) {
          setState("expired");
          setMessage(formatApiError(err) || "This confirmation link has expired.");
        } else {
          setState("error");
          setMessage(formatApiError(err) || "We couldn't confirm your password change.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      data-testid="confirm-password-change-page"
      style={{ background: "#F4F1EA" }}
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-[#E5DFCF] p-10">
        {state === "loading" && (
          <div className="text-center" data-testid="confirm-loading">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#056839]" strokeWidth={1.5} />
            <h1 className="mt-6 text-xl font-semibold text-[#0E1F1A]">
              Confirming your password change…
            </h1>
            <p className="mt-2 text-sm text-[#5E5E5E]">Just a moment.</p>
          </div>
        )}

        {state === "success" && (
          <div className="text-center" data-testid="confirm-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#056839]" strokeWidth={1.5} />
            <h1 className="mt-6 text-xl font-semibold text-[#0E1F1A]">
              Password updated
            </h1>
            <p className="mt-2 text-sm text-[#5E5E5E]">
              Your new password is now active{email ? ` for ${email}` : ""}. Sign in to continue.
            </p>
            <Link
              to="/login"
              data-testid="confirm-go-to-login"
              className="inline-block mt-6 bg-[#056839] hover:bg-[#03522D] text-white text-sm font-medium px-6 py-2.5 rounded-md transition-colors"
            >
              Go to sign in
            </Link>
          </div>
        )}

        {state === "expired" && (
          <div className="text-center" data-testid="confirm-expired">
            <Clock className="w-12 h-12 mx-auto text-[#A0522D]" strokeWidth={1.5} />
            <h1 className="mt-6 text-xl font-semibold text-[#0E1F1A]">
              This link has expired
            </h1>
            <p className="mt-2 text-sm text-[#5E5E5E]">
              For security, confirmation links are valid for only 15 minutes. Sign in and
              request a new password change from your profile page.
            </p>
            <Link
              to="/login"
              data-testid="confirm-go-to-login-expired"
              className="inline-block mt-6 bg-[#056839] hover:bg-[#03522D] text-white text-sm font-medium px-6 py-2.5 rounded-md transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        )}

        {state === "error" && (
          <div className="text-center" data-testid="confirm-error">
            <XCircle className="w-12 h-12 mx-auto text-[#A0522D]" strokeWidth={1.5} />
            <h1 className="mt-6 text-xl font-semibold text-[#0E1F1A]">
              Couldn&apos;t confirm the change
            </h1>
            <p className="mt-2 text-sm text-[#5E5E5E]">{message}</p>
            <Link
              to="/login"
              data-testid="confirm-go-to-login-error"
              className="inline-block mt-6 bg-[#056839] hover:bg-[#03522D] text-white text-sm font-medium px-6 py-2.5 rounded-md transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
